import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { type AtticStockUnitFragment } from "@dashboard/graphql";
import { MONO_FONT_FAMILY } from "@dashboard/styles/monoFontFamily";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { locationMessages, lotMessages, stockMessages } from "../../messages";
import { atticLocationUrl, atticLotUrl, atticStockLookupUrl } from "../../urls";
import { formatCost } from "../../utils";

export interface AtticUnitsTableProps {
  units: AtticStockUnitFragment[];
  /** Which columns to show; a page leaves out the one it is about. */
  show: { sku?: boolean; location?: boolean; lot?: boolean };
  emptyText: string;
}

/** The unit rows of a variant, a location or a lot: oldest lot first, as the API orders them. */
export const AtticUnitsTable = ({ units, show, emptyText }: AtticUnitsTableProps) => {
  const intl = useIntl();
  const columns = 2 + Number(!!show.sku) + Number(!!show.location) + Number(!!show.lot);

  return (
    <ResponsiveTable>
      <TableHead>
        <TableRowLink>
          {show.sku && <TableCell>{intl.formatMessage(locationMessages.sku)}</TableCell>}
          {show.location && <TableCell>{intl.formatMessage(stockMessages.location)}</TableCell>}
          {show.lot && <TableCell>{intl.formatMessage(stockMessages.lot)}</TableCell>}
          <TableCell align="right">{intl.formatMessage(stockMessages.quantity)}</TableCell>
          <TableCell align="right">{intl.formatMessage(stockMessages.unitCost)}</TableCell>
        </TableRowLink>
      </TableHead>
      <TableBody>
        {units.length === 0 ? (
          <TableRowLink data-test-id="attic-units-empty">
            <TableCell colSpan={columns}>
              <Text size={3} color="default2">
                {emptyText}
              </Text>
            </TableCell>
          </TableRowLink>
        ) : (
          units.map(unit => (
            <TableRowLink key={unit.id} data-test-id="attic-unit-row">
              {show.sku && (
                <TableCell>
                  <Link to={atticStockLookupUrl({ sku: unit.sku })}>
                    <Text size={3} style={{ fontFamily: MONO_FONT_FAMILY }}>
                      {unit.sku}
                    </Text>
                  </Link>
                </TableCell>
              )}
              {show.location && (
                <TableCell>
                  <Link to={atticLocationUrl(unit.location.code)}>
                    <Text size={3} style={{ fontFamily: MONO_FONT_FAMILY }}>
                      {unit.location.code}
                    </Text>
                  </Link>
                </TableCell>
              )}
              {show.lot && (
                <TableCell>
                  <Link to={atticLotUrl(unit.lot.id)}>
                    <Text size={3}>
                      {unit.lot.reference || intl.formatMessage(lotMessages.untitled)}
                    </Text>
                  </Link>
                </TableCell>
              )}
              <TableCell align="right">
                <Text size={3}>{unit.quantity}</Text>
              </TableCell>
              <TableCell align="right">
                <Text size={3}>{formatCost(unit.unitCost, unit.lot.currency, intl)}</Text>
              </TableCell>
            </TableRowLink>
          ))
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
